import { useEffect, useState, useMemo } from "react";
import { useLocation } from "react-router-dom";
import {
  subscribeOpenHouses,
  subscribeOpportunities,
  deleteOpenHouse,
  updateOpenHouse,
} from "../../utils/firebasefunctions";
import UpForGrabsBoard from "./UpForGrabsBoard";
import OpportunityApprovalQueue from "./OpportunityApprovalQueue";
import PostUpForGrabsModal from "./PostUpForGrabsModal";
import { deriveStatus, sortOpenHouses, visibleOpenHousesFor } from "../../utils/openHouseHelpers";
import { feedbackUrlFor } from "../../utils/openHouseEmails";
import { sendSchedulingEmails } from "../../utils/openHouseSend";
import OpenHousesTable from "./OpenHousesTable";
import CheckinCenterModal from "./checkin/CheckinCenterModal";
import TemplateEditorModal from "./checkin/TemplateEditorModal";
import QuickCheckinModal from "./checkin/QuickCheckinModal";
import ScheduleModal from "./ScheduleModal";
import CancelModal from "./CancelModal";
import FeedbackModal from "./FeedbackModal";
import RecapPreviewModal from "./RecapPreviewModal";
import RecapPendingBanner from "./RecapPendingBanner";
import { sendOpenHouseEmail } from "../../utils/emailSender";
import { composeInviteResendEmail } from "../../utils/openHouseEmailComposer";
import { useStateValue } from "../../context/StateProvider";
import { toast } from "react-toastify";
import PageHeader from "../PageHeader";
import { SkeletonListItem } from "../Skeleton";
import { useActionDialog } from "../ActionDialog";

const FILTERS = ["all", "upcoming", "completed", "cancelled"];

export default function MainOpenHouses() {
  const [{ user, emailTemplates }] = useStateValue();
  const { confirm, prompt, dialog } = useActionDialog();
  const [allOpenHouses, setAllOpenHouses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");

  const openHouses = useMemo(
    () => visibleOpenHousesFor(allOpenHouses, user),
    [allOpenHouses, user],
  );

  const [scheduleOpen, setScheduleOpen] = useState(false);
  const [editing, setEditing] = useState(null);

  const [cancelTarget, setCancelTarget] = useState(null);
  const [feedbackTarget, setFeedbackTarget] = useState(null);
  const [recapTarget, setRecapTarget] = useState(null);
  const [checkinTarget, setCheckinTarget] = useState(null);
  const [templatesOpen, setTemplatesOpen] = useState(false);
  const [quickCheckinOpen, setQuickCheckinOpen] = useState(false);

  useEffect(() => {
    const unsub = subscribeOpenHouses((rows) => {
      setAllOpenHouses(rows);
      setLoading(false);
    });
    return () => unsub && unsub();
  }, []);

  const location = useLocation();
  const [opportunities, setOpportunities] = useState([]);
  const [tab, setTab] = useState("mine"); // "mine" | "grabs"
  const [postOpen, setPostOpen] = useState(false);

  useEffect(() => {
    const unsub = subscribeOpportunities(setOpportunities);
    return () => unsub && unsub();
  }, []);

  // Deep-link from a notification: /dashboard/open-houses?tab=up-for-grabs
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (params.get("tab") === "up-for-grabs") setTab("grabs");
  }, [location.search]);

  const openGrabCount = useMemo(
    () => opportunities.filter((o) => o.status === "Open").length,
    [opportunities],
  );

  const counts = useMemo(() => {
    const c = { total: openHouses.length, Scheduled: 0, Completed: 0, Cancelled: 0 };
    openHouses.forEach((oh) => {
      c[deriveStatus(oh)] = (c[deriveStatus(oh)] || 0) + 1;
    });
    return c;
  }, [openHouses]);

  // Keep the check-in modal's open house fresh so its first paint has
  // current counters (the modal also subscribes for live ticks itself).
  const liveCheckinTarget = useMemo(
    () => (checkinTarget ? allOpenHouses.find((o) => o.id === checkinTarget.id) || checkinTarget : null),
    [checkinTarget, allOpenHouses],
  );

  const visibleRows = useMemo(() => {
    let rows = openHouses;
    if (filter === "upcoming") rows = rows.filter((oh) => deriveStatus(oh) === "Scheduled");
    else if (filter === "completed") rows = rows.filter((oh) => deriveStatus(oh) === "Completed");
    else if (filter === "cancelled") rows = rows.filter((oh) => deriveStatus(oh) === "Cancelled");
    return sortOpenHouses(rows);
  }, [openHouses, filter]);

  return (
    <div className="h-screen flex flex-col">
      <PageHeader
        title="Open Houses"
        subtitle={`${counts.total} open houses · ${counts.Scheduled || 0} upcoming`}
        actions={
          <div className="flex gap-2">
            <button
              onClick={() => setQuickCheckinOpen(true)}
              className="border border-slate-300 hover:bg-slate-50 text-slate-700 text-sm font-semibold px-3 py-2 rounded-md"
            >
              Quick Start Open House
            </button>
            <button
              onClick={() => setTemplatesOpen(true)}
              className="border border-slate-300 hover:bg-slate-50 text-slate-700 text-sm font-semibold px-3 py-2 rounded-md"
            >
              Check-In Questions
            </button>
            <button
              onClick={() => {
                setEditing(null);
                setScheduleOpen(true);
              }}
              className="bg-slate-800 hover:bg-slate-900 text-white text-sm font-semibold px-3 py-2 rounded-md"
            >
              + Schedule Open House
            </button>
          </div>
        }
      />

      <div className="px-8 pt-4">
        <div className="inline-flex gap-1 bg-slate-100 rounded-lg p-1 mb-3">
          <button
            onClick={() => setTab("mine")}
            className={`px-4 py-1.5 text-xs font-semibold rounded-md ${
              tab === "mine" ? "bg-white text-slate-900 shadow-sm" : "text-slate-500 hover:text-slate-900"
            }`}
          >
            My Open Houses
          </button>
          <button
            onClick={() => setTab("grabs")}
            className={`px-4 py-1.5 text-xs font-semibold rounded-md ${
              tab === "grabs" ? "bg-white text-slate-900 shadow-sm" : "text-slate-500 hover:text-slate-900"
            }`}
          >
            Up For Grabs{openGrabCount ? ` · ${openGrabCount}` : ""}
          </button>
        </div>
        {tab === "mine" && (
          <>
            <RecapPendingBanner openHouses={openHouses} onSent={() => {}} />
            <div className="inline-flex gap-1 bg-slate-100 rounded-lg p-1">
              {FILTERS.map((f) => (
                <button
                  key={f}
                  onClick={() => setFilter(f)}
                  className={`px-4 py-1.5 text-xs font-semibold rounded-md capitalize ${
                    filter === f ? "bg-white text-slate-900 shadow-sm" : "text-slate-500 hover:text-slate-900"
                  }`}
                >
                  {f}
                </button>
              ))}
            </div>
          </>
        )}
      </div>

      {tab === "mine" && (
        <div className="px-8 pt-4 grid grid-cols-4 gap-3">
          <StatCard label="Total" value={counts.total} />
          <StatCard label="Upcoming" value={counts.Scheduled || 0} color="text-blue-700" />
          <StatCard label="Completed" value={counts.Completed || 0} color="text-emerald-700" />
          <StatCard label="Cancelled" value={counts.Cancelled || 0} />
        </div>
      )}

      <div className="px-8 py-4 flex-1 overflow-y-auto">
        {tab === "grabs" ? (
          <>
            <OpportunityApprovalQueue opportunities={opportunities} />
            <UpForGrabsBoard
              opportunities={opportunities}
              onPost={() => setPostOpen(true)}
            />
          </>
        ) : loading ? (
          <div className="space-y-2 max-w-4xl">
            {Array.from({ length: 4 }).map((_, i) => (
              <div
                key={i}
                className="bg-white rounded-lg border border-slate-200"
              >
                <SkeletonListItem />
              </div>
            ))}
          </div>
        ) : (
          <OpenHousesTable
            rows={visibleRows}
            onAddFeedback={setFeedbackTarget}
            onEditFeedback={setFeedbackTarget}
            onEdit={(oh) => {
              setEditing(oh);
              setScheduleOpen(true);
            }}
            onCancel={setCancelTarget}
            onSendRecap={(oh) => setRecapTarget(oh)}
            onCheckin={setCheckinTarget}
            onSendInvites={async (oh) => {
              // Occupied-home flow: seller has now verbally OK'd the time,
              // so we release the hold and fire the original scheduling
              // emails. Confirm first to avoid an accidental click.
              const label =
                oh.propertyAddress?.split(",")[0] || "this open house";
              const ok = await confirm({
                title: "Send notifications",
                body: `Send the open-house notifications for ${label} to the seller and hosting agent now?`,
                confirmLabel: "Send",
              });
              if (!ok) return;
              try {
                await updateOpenHouse(
                  oh.id,
                  { pendingSellerApproval: false },
                  user.id,
                );
                await sendSchedulingEmails(oh.id, user, emailTemplates);
                toast.success("Notifications sent.");
              } catch (err) {
                console.error("Send invites failed:", err);
                toast.error("Could not send notifications. Try again.");
              }
            }}
            onResendInvite={async (oh) => {
              if (!oh.hostingAgentEmail) {
                toast.error("No hosting agent email on file.");
                return;
              }
              const { subject, text, html } = composeInviteResendEmail(oh, user, undefined, emailTemplates?.["scheduling-agent"]);
              const r = await sendOpenHouseEmail({
                userId: user.id,
                to: oh.hostingAgentEmail,
                toName: oh.hostingAgentName,
                subject, text, html,
                type: "invite-resend",
                relatedDealId: oh.dealId,
                relatedOpenHouseId: oh.id,
              });
              if (r.ok) toast.success("Invite re-sent.");
              else toast.error(`Re-send failed: ${r.error}`);
            }}
            onCopyLink={async (oh) => {
              const url = feedbackUrlFor(oh);
              try {
                await navigator.clipboard.writeText(url);
                toast.success("Feedback link copied to clipboard.");
              } catch {
                // Clipboard blocked. Show the link in a read-only field so it
                // can still be selected and copied by hand.
                await prompt({
                  title: "Copy this feedback link",
                  label: "Feedback link",
                  defaultValue: url,
                  readOnly: true,
                  confirmLabel: "Done",
                });
              }
            }}
            onDelete={async (oh) => {
              const label = oh.propertyAddress?.split(",")[0] || "this open house";
              const ok = await confirm({
                title: "Delete Open House",
                body: `Delete ${label} permanently?`,
                note: "This cannot be undone.",
                tone: "danger",
                confirmLabel: "Delete",
              });
              if (!ok) return;
              try {
                await deleteOpenHouse(oh.id);
                toast.success("Open house deleted.");
              } catch (err) {
                console.error("Delete failed:", err);
                toast.error("Could not delete. Try again.");
              }
            }}
            // Closes an open house that happened but produced nothing worth
            // recording. Confirmed because it's one-way: no recap, no feedback
            // afterwards, no restarting it.
            onMarkComplete={async (oh) => {
              const label =
                oh.propertyAddress?.split(",")[0] || "this open house";
              const ok = await confirm({
                title: "Mark complete with no feedback",
                body: `Mark ${label} complete with no feedback?`,
                note: "It closes the open house. You won't be able to add feedback or send a recap afterwards.",
                tone: "danger",
                confirmLabel: "Mark complete",
              });
              if (!ok) return;
              try {
                await updateOpenHouse(oh.id, { status: "Completed" }, user.id);
                toast.success("Open house marked complete.");
              } catch (err) {
                console.error("Mark complete failed:", err);
                toast.error("Could not mark complete. Try again.");
              }
            }}
          />
        )}
      </div>

      <ScheduleModal
        open={scheduleOpen}
        existing={editing}
        onClose={() => setScheduleOpen(false)}
      />
      <CancelModal
        open={!!cancelTarget}
        openHouse={cancelTarget}
        onClose={() => setCancelTarget(null)}
      />
      <FeedbackModal
        open={!!feedbackTarget}
        openHouse={feedbackTarget}
        onClose={() => setFeedbackTarget(null)}
        onSaved={(updated) => {
          setFeedbackTarget(null);
          setRecapTarget(updated);
        }}
      />
      <RecapPreviewModal
        open={!!recapTarget}
        openHouse={recapTarget}
        onClose={() => setRecapTarget(null)}
      />
      <PostUpForGrabsModal open={postOpen} onClose={() => setPostOpen(false)} />
      <CheckinCenterModal
        open={!!checkinTarget}
        openHouse={liveCheckinTarget}
        onClose={() => setCheckinTarget(null)}
      />
      <TemplateEditorModal open={templatesOpen} onClose={() => setTemplatesOpen(false)} />
      <QuickCheckinModal open={quickCheckinOpen} onClose={() => setQuickCheckinOpen(false)} />
      {dialog}
    </div>
  );
}

function StatCard({ label, value, color = "text-slate-900" }) {
  return (
    <div className="border border-slate-200 rounded-lg bg-white p-3">
      <div className="text-xs font-semibold uppercase tracking-wide text-slate-500">{label}</div>
      <div className={`text-2xl font-bold mt-1 ${color}`}>{value}</div>
    </div>
  );
}
